import React, { useCallback, useMemo } from "react";

import ModalField, {
	EditButtonRenderProps
} from "components/ModalField/ModalField";
import { trimText } from "utils/trimText";
import { useModalField } from "hooks/useModalField";

interface ModalFieldTextAreaProps {
	onCancel?: () => void;
	onOK: (newValue: string) => void;
	title: string;
	value: string;
	isUnlocked?: boolean;
	formatValue?: (value: string) => string;
	editButton?: (props: EditButtonRenderProps) => React.ReactNode;
	rows?: number;
}

const ModalFieldTextArea: React.FunctionComponent<ModalFieldTextAreaProps> = ({
	onCancel = () => undefined,
	onOK,
	title,
	value,
	isUnlocked = true,
	formatValue,
	editButton,
	rows = 4
}) => {
	const {
		currentValue,
		setCurrentValue,
		isOpen,
		setIsOpen,
		toggleIsOpen
	} = useModalField({ value });

	const handleOK = useCallback(() => {
		onOK(currentValue);
		setIsOpen(false);
	}, [onOK, currentValue, setIsOpen]);

	const handleCancel = useCallback(() => {
		onCancel();
		setIsOpen(false);
	}, [onCancel, setIsOpen]);

	const formattedValue = useMemo(() => {
		if (formatValue) {
			return formatValue(value);
		}

		return trimText({
			text: value ? value : "",
			max: 100,
			addEllipsis: true
		}).newText;
	}, [formatValue, value]);

	return (
		<ModalField
			isUnlocked={isUnlocked}
			toggleIsOpen={toggleIsOpen}
			displayValue={formattedValue}
			isOpen={isOpen}
			handleOK={handleOK}
			handleCancel={handleCancel}
			title={title}
			allowWrapInButton
		>
			<textarea
				value={currentValue}
				onChange={e => {
					setCurrentValue(e.currentTarget.value);
				}}
				rows={rows}
			/>
		</ModalField>
	);
};

export default ModalFieldTextArea;
